import { useMemo, useState } from "react"
import { Accordion, Alert, Badge, Button, Group, Modal, Select, Stack, Text } from "@mantine/core"
import { IconAlertCircle, IconClockHour4 } from "@tabler/icons-react"
import { useConfirmDeliveryStop, useMergeStops, MergeStopAssignment } from "../hooks/use-delivery-stops"
import { DeliveryStop, DeliveryStopItem, DeliveryStopProducto, PRODUCTO_TIPO, STOP_ACTION } from "../domain/delivery-stop.types"

interface Props {
  stops: DeliveryStop[]
  opened: boolean
  onClose: () => void
  onSaved: (survivors: DeliveryStop[]) => void
}

interface ProductRow {
  originStopId: string
  soId?: number
  soName?: string
  patientName?: string
  tipo: string
  producto: DeliveryStopProducto
}

/** Grupo movible: todos los productos de un mismo tipo, de un mismo paciente/SO, que vinieron del mismo punto */
interface Group {
  key: string // `${originStopId}:${tipo}:${soId ?? "sin-so"}`
  originStopId: string
  tipo: string
  soId?: number
  soName?: string
  patientName?: string
  rows: ProductRow[]
}

function productoKey(p: DeliveryStopProducto): string | null {
  if (p.tipo === PRODUCTO_TIPO.SUELTO && p.pickingId != null && p.productId != null) return `suelto:${p.pickingId}:${p.productId}`
  if (p.tipo === PRODUCTO_TIPO.BANDEJA && p.odooBandejaId != null) return `bandeja:${p.odooBandejaId}`
  if (p.tipo === PRODUCTO_TIPO.ACTIVO && p.activoId != null) return `activo:${p.activoId}`
  if (p.tipo === PRODUCTO_TIPO.PAQUETE && p.paqueteId != null) return `paquete:${p.paqueteId}`
  return null
}

const TIPO_ORDER = [PRODUCTO_TIPO.SUELTO, PRODUCTO_TIPO.BANDEJA, PRODUCTO_TIPO.ACTIVO, PRODUCTO_TIPO.PAQUETE]
const tipoBadgeColor: Record<string, string> = { suelto: "indigo", bandeja: "violet", activo: "orange", paquete: "grape" }
const tipoLabel: Record<string, string> = { suelto: "Productos sueltos", bandeja: "Bandejas", activo: "Activos", paquete: "Paquetes" }

export function MergeStopsModal({ stops, opened, onClose, onSaved }: Props) {
  const mergeMutation = useMergeStops()
  const confirmMutation = useConfirmDeliveryStop()
  const [confirming, setConfirming] = useState(false)

  const { groups, itemTemplateBySo } = useMemo(() => {
    const groups: Group[] = []
    const byKey = new Map<string, Group>()
    const itemTemplateBySo = new Map<number, DeliveryStopItem>()

    for (const stop of stops) {
      for (const item of stop.items ?? []) {
        if (item.soId != null && !itemTemplateBySo.has(item.soId)) {
          itemTemplateBySo.set(item.soId, {
            patientName: item.patientName,
            soId: item.soId,
            soName: item.soName,
            doctorName: item.doctorName,
            surgeryDate: item.surgeryDate,
            isEmergency: item.isEmergency
          })
        }
        for (const p of item.productos ?? []) {
          if (!productoKey(p) || !p.tipo) continue
          const key = `${stop.id}:${p.tipo}:${item.soId ?? "sin-so"}`
          if (!byKey.has(key)) {
            const group: Group = { key, originStopId: stop.id, tipo: p.tipo, soId: item.soId, soName: item.soName, patientName: item.patientName, rows: [] }
            byKey.set(key, group)
            groups.push(group)
          }
          byKey.get(key)!.rows.push({ originStopId: stop.id, soId: item.soId, soName: item.soName, patientName: item.patientName, tipo: p.tipo, producto: p })
        }
      }
    }
    return { groups, itemTemplateBySo }
  }, [stops])

  const [destinationByGroup, setDestinationByGroup] = useState<Record<string, string>>(() => Object.fromEntries(groups.map((g) => [g.key, g.originStopId])))

  const groupsSignature = groups.map((g) => g.key).join("|")
  const [lastSignature, setLastSignature] = useState(groupsSignature)
  if (groupsSignature !== lastSignature) {
    setLastSignature(groupsSignature)
    setDestinationByGroup(Object.fromEntries(groups.map((g) => [g.key, g.originStopId])))
  }

  const stopOptions = stops.map((s) => ({ value: s.id, label: s.correlativo ?? s.id }))

  const buildAssignments = (): { assignments: MergeStopAssignment[]; survivors: DeliveryStop[] } => {
    const byStopAndSo = new Map<string, Map<number, DeliveryStopProducto[]>>()
    for (const stop of stops) byStopAndSo.set(stop.id, new Map())

    for (const group of groups) {
      const destStopId = destinationByGroup[group.key] ?? group.originStopId
      const bySo = byStopAndSo.get(destStopId)
      if (!bySo || group.soId == null) continue
      for (const row of group.rows) {
        if (!bySo.has(group.soId)) bySo.set(group.soId, [])
        bySo.get(group.soId)!.push(row.producto)
      }
    }

    const assignments: MergeStopAssignment[] = stops.map((stop) => {
      const bySo = byStopAndSo.get(stop.id) ?? new Map()
      const items: DeliveryStopItem[] = [...bySo.entries()].map(([soId, productos]) => ({ ...(itemTemplateBySo.get(soId) ?? { soId }), productos }))
      return { stopId: stop.id, items }
    })

    const survivors = stops.filter((stop) => (byStopAndSo.get(stop.id)?.size ?? 0) > 0)
    return { assignments, survivors }
  }

  const handleGuardar = async () => {
    const { assignments, survivors } = buildAssignments()
    await mergeMutation.mutateAsync({ assignments })
    onSaved(survivors)
  }

  const handleGuardarYConfirmar = async () => {
    const { assignments, survivors } = buildAssignments()
    setConfirming(true)
    try {
      await mergeMutation.mutateAsync({ assignments })
      await Promise.all(survivors.map((s) => confirmMutation.mutateAsync(s.id)))
      onSaved(survivors)
    } finally {
      setConfirming(false)
    }
  }

  const emptiedStopIds = stops.filter((stop) => groups.every((g) => (destinationByGroup[g.key] ?? g.originStopId) !== stop.id)).map((s) => s.correlativo ?? s.id)

  if (stops.length < 2) return null

  const saving = mergeMutation.isPending || confirming

  return (
    <Modal opened={opened} onClose={onClose} title="Fusionar puntos de entrega" size="lg" centered>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Para cada grupo de productos, elige a cuál de los puntos seleccionados se queda. El destino (dirección) de cada punto no cambia.
        </Text>

        <Accordion multiple defaultValue={stops.map((s) => s.id)} variant="separated">
          {stops.map((stop) => {
            const stopGroups = groups.filter((g) => (destinationByGroup[g.key] ?? g.originStopId) === stop.id)
            const groupsByTipo = TIPO_ORDER.map((tipo) => ({ tipo, groups: stopGroups.filter((g) => g.tipo === tipo) })).filter((t) => t.groups.length > 0)

            return (
              <Accordion.Item key={stop.id} value={stop.id}>
                <Accordion.Control>
                  <Group gap={8} wrap="nowrap">
                    <Text fw={600} fz="sm" c="indigo">
                      {stop.correlativo ?? stop.id}
                    </Text>
                    <Badge size="xs" variant="light" color="gray">
                      {stopGroups.reduce((acc, g) => acc + g.rows.length, 0)} producto(s)
                    </Badge>
                    <Text fz="xs" c="dimmed" truncate style={{ flex: 1 }}>
                      {stop.address}
                    </Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    {stop.action === STOP_ACTION.DELIVERY && stop.ventanaEntrega && (
                      <Alert icon={<IconClockHour4 size={14} />} color="blue" variant="light" p="xs">
                        <Text size="xs">
                          Ventana de entrega{stop.ventanaEntrega.zonaReparto ? ` (${stop.ventanaEntrega.zonaReparto})` : ""}: recibir antes de las{" "}
                          <strong>{stop.ventanaEntrega.horaLimiteRecepcionImplante ?? "—"}</strong>
                          {stop.ventanaEntrega.tiempoLlegadaMin != null ? ` · ${stop.ventanaEntrega.tiempoLlegadaMin} min de viaje estimado` : ""}
                        </Text>
                      </Alert>
                    )}
                    {stop.action === STOP_ACTION.PICKUP && stop.ventanaRecojo && (
                      <Alert icon={<IconClockHour4 size={14} />} color="blue" variant="light" p="xs">
                        <Text size="xs">
                          Ventana de recojo: {stop.ventanaRecojo.horaRecojoInstrumental ?? "—"}
                          {stop.ventanaRecojo.horaDesde && stop.ventanaRecojo.horaHasta ? ` (rango ${stop.ventanaRecojo.horaDesde}–${stop.ventanaRecojo.horaHasta})` : ""}
                          {stop.ventanaRecojo.diasHabilitados?.length ? ` · días habilitados: ${stop.ventanaRecojo.diasHabilitados.join(", ")}` : ""}
                        </Text>
                      </Alert>
                    )}

                    {groupsByTipo.length === 0 && (
                      <Text size="xs" c="dimmed">
                        Sin productos.
                      </Text>
                    )}

                    {groupsByTipo.map(({ tipo, groups: tipoGroups }) => (
                      <Stack key={tipo} gap={4}>
                        <Badge size="sm" color={tipoBadgeColor[tipo] ?? "gray"} style={{ alignSelf: "flex-start" }}>
                          {tipoLabel[tipo] ?? tipo}
                        </Badge>
                        {tipoGroups.map((group) => (
                          <Group key={group.key} justify="space-between" wrap="nowrap" gap="xs" pl="xs">
                            <Text fz="xs" style={{ flex: 1 }} truncate>
                              {group.rows.length} ítem(s){group.soName ? ` — ${group.soName}` : ""}
                              {group.patientName ? ` · ${group.patientName}` : ""}
                            </Text>
                            <Select
                              size="xs"
                              w={200}
                              allowDeselect={false}
                              value={destinationByGroup[group.key] ?? group.originStopId}
                              onChange={(value) => setDestinationByGroup((prev) => ({ ...prev, [group.key]: value || group.originStopId }))}
                              data={stopOptions}
                            />
                          </Group>
                        ))}
                      </Stack>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )
          })}
        </Accordion>

        {emptiedStopIds.length > 0 && (
          <Alert icon={<IconAlertCircle size={14} />} color="orange" variant="light">
            <Text size="xs">
              Con esta distribución, el/los punto(s) <strong>{emptiedStopIds.join(", ")}</strong> quedarían sin productos — se ocultarán y su correlativo se
              liberará si era el último emitido.
            </Text>
          </Alert>
        )}

        <Group justify="space-between" mt="xs">
          <Button variant="subtle" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Group gap="xs">
            <Button variant="light" color="indigo" loading={mergeMutation.isPending && !confirming} disabled={saving || groups.length === 0} onClick={handleGuardar}>
              Guardar fusión
            </Button>
            <Button color="teal" loading={confirming} disabled={saving || groups.length === 0} onClick={handleGuardarYConfirmar}>
              Guardar fusión y confirmar nuevo punto
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  )
}
